import { Component } from "@angular/core";
import { GridReadyEvent, SelectionChangedEvent } from "@ag-grid-community/core";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { AddonBin, Addon, ItemDetail } from "app/model/entities/entity-model";
import { EntityFns } from "app/shared/entity-fns";
import _ from "lodash";

import {
  DialogBaseComponent,
  DialogParams,
} from "app/shared/dialog-base.component";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { DomainService } from "app/shared/domain.service";
import { BarcodeService } from "app/shared/barcode.service";

export interface AddonBinSplitDialogParams extends DialogParams {
  addonBin: AddonBin;
}

@Component({
  templateUrl: "./addon-bin-split-dialog.component.html",
  standalone: false,
})
export class AddonBinSplitDialogComponent extends DialogBaseComponent<AddonBinSplitDialogParams> {
  addonBin!: AddonBin;
  gridOptions!: GridOptionsExt;
  itemDetails!: ItemDetail[];
  isSplitComplete = false;
  newAddonBin!: AddonBin;
  splitItemDetails!: ItemDetail[];
  selectedCount = 0;

  constructor(
    public config: DynamicDialogConfig<AddonBinSplitDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private barcodeService: BarcodeService
  ) {
    super(config, dialogRef, domainService);

    Object.assign(this.config, {
      header: "Split Items into New Add-On Bin",
      width: "1600px",
      height: "800px",
    });
    this.prepare();
  }

  async prepare() {
    this.addonBin = this.params.addonBin;

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      onSelectionChanged: this.onSelectionChanged,
      rowSelection: { mode: "multiRow", checkboxes: true },
    });

    this.itemDetails = await this.dbQueryService.getItemDetailsForAddonBin(
      this.addonBin.id
    );

    this.isPageReady = true;
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    this.selectedCount = event.api.getSelectedRows().length;
  }

  onGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Inventory Identifier",
        field: "id",
        type: "leftAligned",
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "Manufacturer",
        field: "product.productType.manufacturer.name",
        ...AgFns.flexWidth(100),
      },
      { headerName: "SKU", field: "product.productType.style", ...AgFns.fixWidth(160) },
      { headerName: "Description", field: "product.productType.description", ...AgFns.flexWidth(200) },
      { headerName: "Features", field: "product.featureChoicesExtract", ...AgFns.flexWidth(150) },
      { headerName: "J/O #", field: "joDetail.joHeaderId", ...AgFns.fixWidth(150) },
    ];

    AgFns.initGrid(event, colDefs);
  }

  async ok() {
    const selectedRows = this.gridOptions.apiX!.getSelectedRows() as ItemDetail[];

    if (selectedRows.length === 0) {
      this.dialogService.showOkMessage(
        "Select at least one",
        "You must select at least one Item Detail to split off."
      );
      return false;
    } else if (selectedRows.length === this.itemDetails.length) {
      this.dialogService.showOkMessage(
        "Cannot select all",
        `You cannot select all of the Item Details to be split off.
      You must leave at least 1 ItemDetail left on the original Addon Bin`
      );
      return false;
    }

    const idSet = new Set(selectedRows.map((x) => x.id));

    const clone = EntityFns.cloneStruct(this.addonBin, ["id"]);

    this.newAddonBin = this.uow.createEntity(AddonBin, clone);

    const itdas = this.addonBin.itemDetailAddons.filter((x) =>
      idSet.has(x.itemDetailId)
    );
    itdas.forEach((x) => (x.addonBinId = this.newAddonBin.id));

    await this.dbSaveService.saveChanges();

    this.splitItemDetails = selectedRows;
    this.isSplitComplete = true;
  }

  async printBinLabel() {
    let addons = _.flatMap(this.splitItemDetails, (x) =>
      x.itemDetailAddons
        .filter((itda) => itda.addonBinId === this.newAddonBin.id)
        .map((itda) => itda.addon)
    );
    addons = Array.from(new Set(addons));
    await this.barcodeService.printAddonBinLabels([{ addonBin: this.newAddonBin, addons }], false);
  }

  async printRevisedItemLabels() {
    await this.barcodeService.printItemDetailBarcodes(this.splitItemDetails);
  }

  close() {
    this.dialogRef.close(true);
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
